import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../utils/api';
import { Button } from './ui/button';
import { Mail, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

/**
 * AUTH-02 — Full blocking screen shown inside the consultation flow when
 * `POST /generate-documentation` returns 403 with `error=email_nao_verificado`.
 *
 * Two primary CTAs:
 *   - "Reenviar email de verificação" → POST /api/auth/resend-verification
 *     (with 60s cooldown after each click)
 *   - "Já confirmei — tentar novamente" → refreshUser() then onRetry()
 *
 * ONBOARD-01 refinement — after a successful resend, surface a secondary
 * link ("Não recebeu? Verifique sua caixa de spam ou altere o email de
 * cadastro") so the user can pivot to /settings without leaving the flow.
 */
export const EmailVerificationGate = ({ maskedEmail, onRetry }) => {
  const { user, refreshUser } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [cooldown, setCooldown] = useState(0);
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [resentOnce, setResentOnce] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const handleResend = async () => {
    if (cooldown > 0 || sending) return;
    setSending(true);
    try {
      const res = await api.resendVerification();
      toast.success(res?.message || 'Email de verificação enviado.');
      setCooldown(Number(res?.cooldown_seconds) || 60);
      setResentOnce(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429 && typeof detail === 'object') {
        const remaining = Number(detail?.retry_after_seconds) || 60;
        setCooldown(remaining);
        toast.error(detail?.message || `Aguarde ${remaining}s antes de reenviar.`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Não foi possível reenviar agora. Tente novamente.');
      }
    } finally {
      setSending(false);
    }
  };

  const handleRetry = async () => {
    setChecking(true);
    try {
      const fresh = await refreshUser();
      if (fresh && fresh.email_verified === true) {
        toast.success('Email verificado! Continuando...');
        onRetry?.();
      } else {
        toast.info('Ainda não verificado. Confirme pelo link no seu email.');
      }
    } finally {
      setChecking(false);
    }
  };

  const cardBg = isDark
    ? 'bg-slate-800/60 border-slate-700 text-slate-100'
    : 'bg-white border-slate-200 text-slate-900';
  const muted = isDark ? 'text-slate-400' : 'text-slate-600';
  const email = maskedEmail || user?.email || '';

  return (
    <div
      className={`max-w-xl mx-auto rounded-2xl border p-8 text-center ${cardBg}`}
      data-testid="email-verification-gate"
    >
      <div
        className="w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center"
        style={{ backgroundColor: 'rgba(249, 115, 22, 0.12)' }}
      >
        <Mail className="w-8 h-8 text-primary" />
      </div>
      <h2 className="text-2xl font-semibold mb-3" data-testid="gate-title">
        Confirme seu email para continuar
      </h2>
      <p className={`text-sm ${muted} mb-6 leading-relaxed`}>
        Enviamos um link de confirmação para{' '}
        <span className="font-mono font-medium" data-testid="gate-masked-email">{email}</span>.
        Clique no link para desbloquear a geração de prontuários.
      </p>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button
          onClick={handleResend}
          disabled={cooldown > 0 || sending}
          variant="outline"
          data-testid="gate-resend-btn"
        >
          {sending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {cooldown > 0 ? `Aguarde ${cooldown}s...` : 'Reenviar email de verificação'}
        </Button>
        <Button
          onClick={handleRetry}
          disabled={checking}
          className="bg-primary hover:bg-primary-hover"
          data-testid="gate-retry-btn"
        >
          {checking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Já confirmei — tentar novamente
        </Button>
      </div>

      {/* ONBOARD-01 refinement — escape hatch after the first resend click */}
      {resentOnce && (
        <p
          className={`text-xs mt-5 ${muted}`}
          data-testid="gate-spam-help"
        >
          Não recebeu? Verifique sua caixa de spam ou{' '}
          <button
            type="button"
            onClick={() => navigate('/settings')}
            className="underline underline-offset-2 font-medium hover:opacity-80"
            data-testid="gate-change-email-link"
          >
            altere o email de cadastro
          </button>
          .
        </p>
      )}
    </div>
  );
};

export default EmailVerificationGate;

